// DefaultRecordValueInput.jsx
import React from 'react';
import styles from './DefaultRecordValueInput.module.css';
import dataTypesConfig from '../../constants/dataTypes.json';

const supportedTypes = new Set([
  dataTypesConfig.supportedTypes.STRING,
  dataTypesConfig.supportedTypes.NAME,
  dataTypesConfig.supportedTypes.UINT128_T,
  dataTypesConfig.supportedTypes.UINT64_T,
  dataTypesConfig.supportedTypes.UINT32_T,
  dataTypesConfig.supportedTypes.UINT16_T,
  dataTypesConfig.supportedTypes.UINT8_T,
  dataTypesConfig.supportedTypes.INT64_T,
  dataTypesConfig.supportedTypes.INT32_T,
  dataTypesConfig.supportedTypes.INT16_T,
  dataTypesConfig.supportedTypes.INT8_T,
  dataTypesConfig.supportedTypes.DOUBLE,
  dataTypesConfig.supportedTypes.FLOAT,
  dataTypesConfig.supportedTypes.BOOL,
  dataTypesConfig.supportedTypes.TIME_POINT,
]);

const DefaultRecordValueInput = ({ type, value, onChange }) => {
  if (!supportedTypes.has(type)) return null;

  const handleTextChange = (e) => onChange(e.target.value);

  const handleNumberChange = (e) => {
    const val = e.target.value;
    onChange(val === '' ? '' : Number(val));
  };

  const handleBooleanChange = () => {
    // Three-state cycle: undefined → true → false → undefined
    if (value === undefined || value === null || value === '') {
      onChange('true'); // First click: set to true
    } else if (value === 'true' || value === true) {
      onChange('false'); // Second click: set to false
    } else {
      onChange(''); // Third click: remove default value
    }
  };

  const handleDateChange = (e) => {
    onChange(e.target.value);
  };

  const handleNowToggle = (e) => {
    if (e.target.checked) {
      onChange('NOW()');
      return;
    }
    onChange('');
  };

  switch (type) {
    case dataTypesConfig.supportedTypes.INT64_T:
    case dataTypesConfig.supportedTypes.INT32_T:
    case dataTypesConfig.supportedTypes.INT16_T:
    case dataTypesConfig.supportedTypes.INT8_T:
    case dataTypesConfig.supportedTypes.UINT128_T:
    case dataTypesConfig.supportedTypes.UINT64_T:
    case dataTypesConfig.supportedTypes.UINT32_T:
    case dataTypesConfig.supportedTypes.UINT16_T:
    case dataTypesConfig.supportedTypes.UINT8_T:
      return (
        <input
          type="number"
          step="1"
          className={styles.edit_input_value}
          value={value === undefined || value === null ? '' : value}
          onChange={handleNumberChange}
          min={type.startsWith('uint') ? 0 : undefined}
        />
      );

    case dataTypesConfig.supportedTypes.DOUBLE:
    case dataTypesConfig.supportedTypes.FLOAT:
      return (
        <input
          type="number"
          step="any"
          className={styles.edit_input_value}
          value={value === undefined || value === null ? '' : value}
          onChange={handleNumberChange}
        />
      );

    case dataTypesConfig.supportedTypes.BOOL:
      // Determine the current state
      const isChecked = value === 'true' || value === true;
      const isIndeterminate = value === undefined || value === null || value === '';
      
      return (
        <div className={styles.bool_wrapper}>
          <input
            type="checkbox"
            className={`${styles.edit_input_value} ${styles.bool_checkbox}`}
            checked={isChecked}
            onChange={handleBooleanChange}
            ref={(el) => {
              if (el) {
                el.indeterminate = isIndeterminate;
              }
            }}
          />
        </div>
      );

    case dataTypesConfig.supportedTypes.TIME_POINT:
      const isNowSelected = value === 'NOW()';
      return (
        <div className={styles.timepoint_wrapper}>
          <label className={styles.now_toggle}>
            <input
              type="checkbox"
              checked={isNowSelected}
              onChange={handleNowToggle}
            />
            NOW()
          </label>
          <input
            type="datetime-local"
            className={styles.edit_input_value}
            value={isNowSelected ? '' : (value || '')}
            onChange={handleDateChange}
            disabled={isNowSelected}
          />
        </div>
      );

    case dataTypesConfig.supportedTypes.NAME:
    case dataTypesConfig.supportedTypes.STRING:
    default:
      return (
        <input
          type="text"
          className={styles.edit_input_value}
          value={value || ''}
          onChange={handleTextChange}
        />
      );
  }
};

export default DefaultRecordValueInput;
